import { ReactNode, useId } from 'react';

import classNames from 'classnames';

import Typography from '@mui/material/Typography';

export type SimpleTableProps = {
  title?: string;
  titleClass?: string;
  headingClass?: string;
  dataClass?: string;
  rows: Array<{
    label: string;
    value: string | number | ReactNode;
  }>;
  'aria-label'?: string;
  'aria-describedby'?: string;
};

export const SimpleTable = ({
  rows,
  title,
  titleClass,
  headingClass,
  dataClass,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby
}: SimpleTableProps) => {
  const tableId = useId();
  const captionId = title ? `${tableId}-caption` : undefined;

  return (
    <table
      className="w-full text-left"
      aria-label={ariaLabel || (title ? `${title}` : 'Data table')}
      aria-describedby={ariaDescribedby}
      role="table"
      tabIndex={0}
    >
      {title && (
        <caption id={captionId} translate="no" className="mb-[16px] text-left">
          <Typography
            variant="headlineXLGBold"
            className={classNames('text-navigation-80', titleClass)}
          >
            {title}
          </Typography>
        </caption>
      )}
      <tbody>
        {rows.map((row, index) => (
          <tr
            key={`${tableId}-row-${index}`}
            className="border-solid border-b-[1px] border-l-0 border-r-0 border-t-0 border-b-text-20"
            role="row"
          >
            <th
              scope="row"
              translate="no"
              className="w-1/2 overflow-hidden text-ellipsis align-top"
              role="rowheader"
              tabIndex={0}
              onFocus={(e) => {
                e.currentTarget.setAttribute('aria-selected', 'true');
              }}
              onBlur={(e) => {
                e.currentTarget.removeAttribute('aria-selected');
              }}
            >
              <Typography
                variant="bodySMBold"
                className={classNames('text-text-80 py-[8px]', headingClass)}
              >
                {row.label}
              </Typography>
            </th>
            <td
              translate="no"
              className="w-1/2 overflow-hidden text-ellipsis align-top"
              role="cell"
              tabIndex={0}
              onFocus={(e) => {
                e.currentTarget.setAttribute('aria-selected', 'true');
              }}
              onBlur={(e) => {
                e.currentTarget.removeAttribute('aria-selected');
              }}
            >
              <Typography
                variant="bodySMRegular"
                className={classNames('text-text-100 py-[8px]', dataClass)}
              >
                {row.value}
              </Typography>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
